import React from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import Button from '../ui/Button';
import { PLANS, ENTERPRISE_PLAN, TRIAL_DURATION_DAYS } from '../../config/plans';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  font-family: ${theme.typography.fontFamily};
  padding: ${theme.spacing.xl};
`;

const Modal = styled.div`
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing.xl};
  max-width: 1000px;
  width: 100%;
  box-shadow: ${theme.shadows.lg};
  border: 1px solid ${theme.colors.border};
  position: relative;
  max-height: 90vh;
  overflow-y: auto;
`;

const CloseButton = styled.button`
  position: absolute;
  top: ${theme.spacing.md};
  right: ${theme.spacing.md};
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSizes.xxxl};
  color: ${theme.colors.text};
  cursor: pointer;
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  line-height: 1;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${theme.colors.surfaceHover};
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
  }
`;

const TrialHighlight = styled.div`
  background: ${theme.colors.background};
  color: ${theme.colors.accent};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  text-align: center;
  font-size: ${theme.typography.fontSizes.base};
  font-weight: ${theme.typography.fontWeights.semibold};
  margin-bottom: ${theme.spacing.lg};
  letter-spacing: ${theme.typography.letterSpacing.normal};
`;

const Title = styled.h2`
  margin: 0 0 ${theme.spacing.xs} 0;
  color: ${theme.colors.text};
  text-align: center;
  font-size: ${theme.typography.fontSizes.xxxl};
  font-weight: ${theme.typography.fontWeights.bold};
  letter-spacing: ${theme.typography.letterSpacing.tight};
`;

const Subtitle = styled.p`
  margin: 0 0 ${theme.spacing.lg} 0;
  color: ${theme.colors.text};
  text-align: center;
  font-size: ${theme.typography.fontSizes.base};
  font-weight: ${theme.typography.fontWeights.medium};
  opacity: 0.9;
`;

const PlansGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.lg};
  
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const PlanCard = styled.div`
  background: ${theme.colors.background};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing.lg};
  display: flex;
  flex-direction: column;
  position: relative;
  transition: all 0.3s ease;
  align-items: stretch;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: ${theme.shadows.lg};
    border-color: ${theme.colors.primary};
  }
`;

const PlanName = styled.h3`
  font-size: ${theme.typography.fontSizes.xl};
  font-weight: ${theme.typography.fontWeights.bold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.md} 0;
  text-align: center;
  letter-spacing: ${theme.typography.letterSpacing.tight};
`;

const PlanPrice = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing.md};
`;

const Price = styled.div`
  font-size: 36px;
  font-weight: ${theme.typography.fontWeights.bold};
  color: ${theme.colors.primary};
  line-height: 1;
  margin-bottom: ${theme.spacing.xs};
  
  span {
    font-size: ${theme.typography.fontSizes.sm};
    color: ${theme.colors.text};
    font-weight: ${theme.typography.fontWeights.normal};
    opacity: 0.7;
  }
`;

const PlanDescription = styled.div`
  font-size: ${theme.typography.fontSizes.sm};
  color: ${theme.colors.text};
  text-align: center;
  margin-bottom: ${theme.spacing.md};
  font-weight: ${theme.typography.fontWeights.medium};
  opacity: 0.85;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 ${theme.spacing.md} 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

const Feature = styled.li`
  padding: 0;
  color: ${theme.colors.text};
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing.sm};
  font-size: ${theme.typography.fontSizes.sm};
  line-height: 1.4;
  font-weight: ${theme.typography.fontWeights.normal};
  
  &:before {
    content: '✓';
    color: ${theme.colors.success};
    font-weight: bold;
    font-size: ${theme.typography.fontSizes.base};
    flex-shrink: 0;
    margin-top: 1px;
  }
`;

const CustomPrice = styled.div`
  font-size: 28px;
  font-weight: ${theme.typography.fontWeights.bold};
  color: ${theme.colors.primary};
  line-height: 1;
  margin-bottom: ${theme.spacing.xs};
`;

const ContactButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  background: transparent;
  color: ${theme.colors.textSecondary};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSizes.sm};
  font-weight: ${theme.typography.fontWeights.semibold};
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: auto;
  
  &:hover {
    background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primaryDark} 100%);
    color: white;
    border-color: transparent;
  }
`;

const CTASection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: ${theme.spacing.md};
  border-top: 1px solid ${theme.colors.border};
  margin-top: ${theme.spacing.md};
`;

const CTAText = styled.p`
  margin: 0 0 ${theme.spacing.md} 0;
  color: ${theme.colors.text};
  font-size: ${theme.typography.fontSizes.sm};
  font-weight: ${theme.typography.fontWeights.normal};
  opacity: 0.8;
`;

const PricingModal = ({ isOpen, onClose, onSignupClick }) => {
  if (!isOpen) return null;

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>&times;</CloseButton>

        <TrialHighlight>
          Start with a {TRIAL_DURATION_DAYS}-Day Free Trial
        </TrialHighlight>

        <Title>AI-Assisted Video Annotation</Title>
        <Subtitle>Real-world coordinates</Subtitle>

        <PlansGrid>
          {Object.values(PLANS).map((plan) => (
            <PlanCard key={plan.id}>
              <PlanName>{plan.name}</PlanName>
              <PlanPrice>
                <Price>
                  ${plan.price}<span>/{plan.period}</span>
                </Price>
              </PlanPrice>
              <PlanDescription>{plan.description}</PlanDescription>
              <FeatureList>
                {plan.features.map((feature, index) => (
                  <Feature key={index}>{feature}</Feature>
                ))}
              </FeatureList>
            </PlanCard>
          ))}

          {/* Enterprise - display only */}
          <PlanCard>
            <PlanName>{ENTERPRISE_PLAN.name}</PlanName>
            <PlanPrice>
              <CustomPrice>Custom</CustomPrice>
            </PlanPrice>
            <PlanDescription>{ENTERPRISE_PLAN.description}</PlanDescription>
            <FeatureList>
              {ENTERPRISE_PLAN.features.map((feature, index) => (
                <Feature key={index}>{feature}</Feature>
              ))}
            </FeatureList>
            <ContactButton href="mailto:info@splitstep.ai?subject=Enterprise%20Inquiry">
              Contact Us
            </ContactButton>
          </PlanCard>
        </PlansGrid>

        <CTASection>
          <CTAText>
            No credit card required
          </CTAText>
          <Button
            onClick={onSignupClick}
            style={{
              width: '100%',
              maxWidth: '400px',
              margin: '0 auto',
              background: `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primaryDark} 100%)`,
              border: 'none',
              fontSize: theme.typography.fontSizes.base,
              fontWeight: theme.typography.fontWeights.bold,
              padding: `${theme.spacing.md} ${theme.spacing.lg}`
            }}
          >
            Start Free Trial
          </Button>
        </CTASection>
      </Modal>
    </Overlay>
  );
};

export default PricingModal;

